import type { TConversationBootstrapResponse } from "../types/agent";
import type {
	TDatasetCreationData,
	TDatasetCreateResponse,
	TDatasetGeneralMetaResponse,
	TDatasetGuidedQuestionsResponse,
	TDatasetGuidedStepPath,
	TDatasetGuidedStepResult,
	TDatasetMetaResponse,
	TDatasetQuestionRequestObject,
	TLedgerSearchResponseProps,
} from "../types/ledger.type";
import type {
	TQuickStartResponse,
	TDatasetQuickStartQuestions,
} from "../types/quickStart.type";
import type { TTapeSearchParameters } from "../types/tapes.type";
import type { TAvailableColumnsResponse } from "../utils/guidedAvailableColumns";
import type { AxiosRequestConfig } from "axios";

import { getEndpoint } from "../endpoints";

import {
	httpServiceInstance,
	type HttpService,
} from "./http.service";

export class LedgerService {
	private static http: HttpService;
	private static endpoint: string;
	private static base_url: string;
	constructor() {
		LedgerService.http = httpServiceInstance;
		LedgerService.endpoint = getEndpoint();
		LedgerService.base_url = `${LedgerService.endpoint}/api/dataset`;
	}

	async fileUpload(
		formData: FormData,
		datasetId: string,
		isStatic: boolean = false
	): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/${isStatic ? "static" : "perf"}/upload`;
		const config = {
			data: formData,
			headers: {
				"Content-Type": "multipart/form-data",
			},
		};

		const { data } = await LedgerService.http.post<any>(
			url,
			config
		);
		return data;
	}

	async getDatasetGeneralMeta(): Promise<TDatasetGeneralMetaResponse> {
		const url = `${LedgerService.base_url}/meta`;
		const { data } =
			await LedgerService.http.get<TDatasetGeneralMetaResponse>(
				url
			);
		return data;
	}

	async getDatasetMeta(
		datasetId: string
	): Promise<TDatasetMetaResponse> {
		const url = `${LedgerService.base_url}/${datasetId}/meta`;
		const { data } =
			await LedgerService.http.get<TDatasetMetaResponse>(
				url
			);
		return data;
	}

	async getDatasetConversation(
		datasetId: string,
		options?: {
			forceCreate?: boolean;
		}
	): Promise<TConversationBootstrapResponse> {
		const url = `${LedgerService.base_url}/${datasetId}/conversation`;
		const requestData: Record<string, boolean> = {};
		if (options?.forceCreate) {
			requestData.force_create = true;
		}

		const { data } =
			await LedgerService.http.post<TConversationBootstrapResponse>(
				url,
				Object.keys(requestData).length > 0
					? {
							data: requestData,
						}
					: undefined
			);
		return data;
	}

	async createDataset(
		datasetCreationData: TDatasetCreationData
	): Promise<TDatasetCreateResponse> {
		const url = `${LedgerService.base_url}`;
		const { data } =
			await LedgerService.http.post<TDatasetCreateResponse>(
				url,
				{
					data: {
						dataset_name: datasetCreationData.datasetName,
						signature_id: datasetCreationData.signatureId,
						dataset_scope: datasetCreationData.datasetScope,
						dataset_creation_type:
							datasetCreationData.datasetCreationType,
					},
				}
			);
		return data;
	}

	async getQuickStartQuestions(
		datasetId: string
	): Promise<TDatasetQuickStartQuestions> {
		const { data } =
			await LedgerService.http.get<TDatasetQuickStartQuestions>(
				`${LedgerService.base_url}/${datasetId}/ug_quick_start/questions`
			);
		return data;
	}

	async getQuickStart(
		datasetId: string
	): Promise<TQuickStartResponse> {
		const { data } =
			await LedgerService.http.get<TQuickStartResponse>(
				`${LedgerService.base_url}/${datasetId}/ug_quick_start`
			);
		return data;
	}

	async startQuickStart(
		datasetId: string,
		skip = false
	): Promise<TQuickStartResponse> {
		const { data } =
			await LedgerService.http.post<TQuickStartResponse>(
				`${LedgerService.base_url}/${datasetId}/ug_quick_start${skip ? "/skip" : ""}`,
				skip
					? undefined
					: { data: { is_step_confirmed: true } }
			);
		return data;
	}

	async reopenStep(
		datasetId: string,
		step: string
	): Promise<TQuickStartResponse> {
		const { data } =
			await LedgerService.http.post<TQuickStartResponse>(
				`${LedgerService.base_url}/${datasetId}/reopen`,
				{ data: { step } }
			);
		return data;
	}

	async getGuidedStepQuestions(
		datasetId: string,
		stepPath: TDatasetGuidedStepPath
	): Promise<TDatasetGuidedQuestionsResponse> {
		const url = `${LedgerService.base_url}/${datasetId}/${stepPath}/questions`;
		const { data } =
			await LedgerService.http.get<TDatasetGuidedQuestionsResponse>(
				url
			);
		return data;
	}

	async getAvailableColumns(
		datasetId: string
	): Promise<TAvailableColumnsResponse> {
		const url = `${LedgerService.base_url}/${datasetId}/available_cols`;
		const { data } =
			await LedgerService.http.get<TAvailableColumnsResponse>(
				url
			);
		return data;
	}

	async getGuidedStepResult(
		datasetId: string,
		stepPath: TDatasetGuidedStepPath
	): Promise<TDatasetGuidedStepResult> {
		const url = `${LedgerService.base_url}/${datasetId}/${stepPath}`;
		const { data } =
			await LedgerService.http.get<TDatasetGuidedStepResult>(
				url
			);
		return data;
	}

	async submitGuidedStep(
		datasetId: string,
		stepPath: TDatasetGuidedStepPath,
		isStepConfirmed: boolean
	): Promise<TDatasetGuidedStepResult> {
		const url = `${LedgerService.base_url}/${datasetId}/${stepPath}`;
		const { data } =
			await LedgerService.http.post<TDatasetGuidedStepResult>(
				url,
				{ data: { is_step_confirmed: isStepConfirmed } }
			);
		return data;
	}

	async getStaticDataRange(
		datasetId: string
	): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/static/data_range`;
		const { data } = await LedgerService.http.get<any>(url);
		return data;
	}

	async updateStaticDataRange(
		data: any,
		datasetId: string
	): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/static/data_range`;
		const config = {
			data,
		};

		const { data: response } =
			await LedgerService.http.post<any>(url, config);
		return response;
	}

	async getPerfDataRange(datasetId: string): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/perf/data_range`;
		const { data } = await LedgerService.http.get<any>(url);
		return data;
	}

	async updatePerfDataRange(
		data: any,
		datasetId: string
	): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/perf/data_range`;
		const config = {
			data,
		};

		const { data: response } =
			await LedgerService.http.post<any>(url, config);
		return response;
	}

	async checkForUniqueDatasetName(
		datasetName: string,
		abortSignal: AbortSignal
	): Promise<any> {
		const url = `${LedgerService.base_url}/does_name_exist`;
		const config = {
			data: {
				dataset_name: datasetName,
			},
			signal: abortSignal,
		};
		const { data: response } =
			await LedgerService.http.post<any>(url, config);
		return response;
	}

	async datasetSearch(
		request: TTapeSearchParameters,
		config?: AxiosRequestConfig
	): Promise<TLedgerSearchResponseProps> {
		const url = `${LedgerService.base_url}/search?page=${request.page}`;
		try {
			const { data } =
				await LedgerService.http.post<TLedgerSearchResponseProps>(
					url,
					{
						data: request.form,
						...config,
					}
				);
			return data;
		} catch (error: any) {
			return {
				datasets: [],
				count: 0,
				error: error.response,
			};
		}
	}

	async getDatasetSpecsQuestions(
		datasetId: string
	): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/specs/questions`;
		const { data } = await LedgerService.http.get<any>(url);
		return data;
	}

	async updateDatasetSpecsQuestions(
		requestObject: TDatasetQuestionRequestObject
	): Promise<any> {
		const datasetId = requestObject.datasetId;
		const url = `${LedgerService.base_url}/${datasetId}/specs/questions`;
		const { data } = await LedgerService.http.post<any>(
			url,
			{
				data: {
					question_id: requestObject.questionId,
					question_category_id:
						requestObject.questionCategoryId,
					user_input: requestObject.userInput,
					user_input_type: requestObject.userInputType,
				},
			}
		);
		return data;
	}

	async confirmSpecs(datasetId: string): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/specs`;
		const { data } = await LedgerService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: true,
				},
			}
		);
		return data;
	}

	async getDatasetStats(datasetId: string): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/stats`;
		const { data } = await LedgerService.http.get<any>(url);
		return data;
	}

	async rerunDatasetStats(
		datasetId: string,
		dashboardParams: Record<string, any> = {}
	): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/stats`;
		const config = {
			data: {
				is_step_confirmed: false,
				...dashboardParams,
			},
		};
		const { data: response } =
			await LedgerService.http.post<any>(url, config);
		return response;
	}

	async confirmDatasetStats(
		datasetId: string
	): Promise<any> {
		const url = `${LedgerService.base_url}/${datasetId}/confirm`;
		const { data } =
			await LedgerService.http.post<any>(url);
		return data;
	}
}
